import { useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { IBomPart } from 'bom/types';
import { AlternateVersionHelper } from 'core/helpers';
import { AddBomPartsToQuote, IAddBomPartsToQuoteProps, IMapperParams } from 'quotes/components';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IBaseQuote, ICustomerQuote, IQuotePart } from 'quotes/types';

import { mapAddPartsToQuoteForm } from './mappers';
import { ItemsToCopyFormFields } from './Steps/SelectItemsToCopy/ItemsToCopyFormFields';
import { getSchema } from './Steps/SelectItemsToCopy/schema';
import {
  ICreateCustomerQuoteFromQuoteForm,
  ICreateVendorQuoteFromQuoteForm,
} from './Steps/SelectItemsToCopy/types';
import { getItemsToCopyValues } from './Steps/SelectItemsToCopy/utils';

interface IProps {
  addToQuoteType: QuoteType;
  onClose: () => void;
  quote: ICustomerQuote;
  quoteParts: IQuotePart[];
  onSubmitSuccess?: () => void;
  entityType?: QuoteEntityType;
}

export const QuotePartAddToQuote = ({
  addToQuoteType,
  onClose,
  quote,
  quoteParts,
  onSubmitSuccess,
  entityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const { user } = useAuthUser();
  const { addPartsToCustomerQuote, addPartsToVendorQuote } = useQuoteActions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const fromQuoteType = useMemo(() => {
    return quote.type;
  }, [quote.type]);

  const itemsToCopyValues = useMemo(() => {
    return getItemsToCopyValues(fromQuoteType, addToQuoteType);
  }, [fromQuoteType, addToQuoteType]);

  const mapperFunction = useMemo(
    () =>
      (
        params: IMapperParams<ICreateCustomerQuoteFromQuoteForm | ICreateVendorQuoteFromQuoteForm>
      ) => {
        return mapAddPartsToQuoteForm({
          sourceQuote: params.sourceQuote || quote,
          targetQuote: params.targetQuote,
          toQuoteType: params.toQuoteType,
          data: params.data,
          quotePartIds: params.selectedItems.map((quotePart: IBomPart) => quotePart.id),
          entityType: params.entityType,
          isAlternateVersionEnabled: params.isAlternateVersionEnabled,
        });
      },
    [quote]
  );

  const addBomPartsToQuoteProps: IAddBomPartsToQuoteProps<
    ICreateCustomerQuoteFromQuoteForm | ICreateVendorQuoteFromQuoteForm
  > = {
    addToQuoteType,
    onClose,
    onSubmitSuccess,
    selectedItems: quoteParts,
    sourceQuote: quote,
    entityType,
    mapperFunction,
    addToCustomerQuoteAction: addPartsToCustomerQuote as (payload: unknown) => Promise<unknown>,
    addToVendorQuoteAction: addPartsToVendorQuote as (payload: unknown) => Promise<unknown>,
    cliJobInProgressMessageKey: 'messagePartsAddingInProgress',
    cliJobSuccessMessageKey: 'messagePartsAddingSuccess',
    itemsToCopyProps: {
      getSchema: (selectedQuote?: IBaseQuote) =>
        getSchema({
          selectedQuoteId: selectedQuote?.id,
          fromQuoteType,
          addToQuoteType,
          entityType,
          companySettings: user.companySettings,
          isAlternateVersionEnabled,
          itemsToCopyValues,
        }),
      fields: (
        <ItemsToCopyFormFields
          quote={quote}
          addToQuoteType={addToQuoteType}
          itemsToCopyValues={itemsToCopyValues}
        />
      ),
      defaultValues: { pricingDates: true },
    },
  };

  return (
    <AddBomPartsToQuote<ICreateCustomerQuoteFromQuoteForm | ICreateVendorQuoteFromQuoteForm>
      {...addBomPartsToQuoteProps}
    />
  );
};
